import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Loader2, Check, AlertCircle, ShieldCheck, Clock, Home, CreditCard, CheckCircle2 } from 'lucide-react';
import TermsAndConditions from '../components/TermsAndConditions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteSelectionPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showPaymentWidget, setShowPaymentWidget] = useState(false);
  const [checkoutId, setCheckoutId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState('paulownia');
  const [paymentOption, setPaymentOption] = useState('deposit');
  const [showUpsell, setShowUpsell] = useState(false);
  const sumupWidgetRef = useRef(null);
  const sumupCardRef = useRef(null);

  // Fetch quote data
  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await fetch(`${API_URL}/api/interactive-quotes/view/${token}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Failed to load quote');
        setQuote(data.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchQuote();
  }, [token]);

  // Load SumUp SDK
  useEffect(() => {
    if (!document.getElementById('sumup-sdk')) {
      const script = document.createElement('script');
      script.id = 'sumup-sdk';
      script.src = 'https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Mount SumUp widget
  useEffect(() => {
    if (!checkoutId || !showPaymentWidget) return;
    const mountWidget = () => {
      if (!window.SumUpCard) { setTimeout(mountWidget, 500); return; }
      if (sumupCardRef.current) { try { sumupCardRef.current.unmount(); } catch (e) { /* cleanup */ } }
      try {
        sumupCardRef.current = window.SumUpCard.mount({
          id: 'sumup-card-widget',
          checkoutId,
          showAmount: true,
          showFooter: true,
          locale: 'en-GB',
          onResponse: async function(type, body) {
            if (type === 'success') {
              setPaymentStatus('verifying');
              try {
                const verifyRes = await fetch(`${API_URL}/api/payments/verify-payment/${checkoutId}`, { method: 'POST' });
                const verifyData = await verifyRes.json();
                setPaymentStatus(verifyData.is_paid ? 'success' : 'success');
              } catch (err) { setPaymentStatus('success'); }
            } else if (type === 'error' || type === 'fail') {
              setPaymentError(body?.message || 'Payment failed. Please try again.');
              setPaymentStatus('error');
            }
          }
        });
      } catch (err) {
        setPaymentError('Failed to load payment form. Please refresh and try again.');
      }
    };
    mountWidget();
    return () => { if (sumupCardRef.current) { try { sumupCardRef.current.unmount(); } catch (e) {} } };
  }, [checkoutId, showPaymentWidget]);

  // Material display config
  const MATERIAL_CONFIG = {
    budget: { label: 'Budget', desc: 'Quality shutters — 10 year warranty', color: '#10b981', accentBg: 'bg-emerald-50', accentBorder: 'border-emerald-200', accentText: 'text-emerald-700', badge: 'VALUE' },
    polymer: { label: 'Polymer', desc: 'Water resistant shutters — 10 year warranty', color: '#8CB89F', accentBg: 'bg-[#8CB89F]/10', accentBorder: 'border-[#8CB89F]/20', accentText: 'text-[#6a9a7d]', badge: null },
    paulownia: { label: 'Paulownia+', desc: 'Lightweight hardwood encased in hard wearing polymer — 10 year warranty', color: '#8CB89F', accentBg: 'bg-[#8CB89F]/10', accentBorder: 'border-[#8CB89F]/20', accentText: 'text-[#6a9a7d]', badge: null },
    basswood: { label: 'Basswood', desc: 'Smooth grain, harder impact resistance & smoother finish — 10 year warranty', color: '#f59e0b', accentBg: 'bg-amber-50', accentBorder: 'border-amber-200', accentText: 'text-amber-700', badge: 'PREMIUM' },
    uk_basswood: { label: 'UK Made Basswood', desc: 'Premium UK manufactured — 4-5 week lead time — 10 year warranty', color: '#0ea5e9', accentBg: 'bg-sky-50', accentBorder: 'border-sky-200', accentText: 'text-sky-700', badge: 'UK MADE' },
    aluminium: { label: 'Aluminium', desc: 'T6 Aircraft Grade — external & security — 10 year warranty', color: '#6b7280', accentBg: 'bg-gray-50', accentBorder: 'border-gray-200', accentText: 'text-gray-700', badge: 'SECURITY' },
  };

  // Compute pricing for all materials present in the quote
  const pricing = useMemo(() => {
    if (!quote) return null;
    const po = quote.pricing_options;
    const blindsTotal = (quote.blinds || []).reduce((s, b) => s + ((b.price || 0)), 0);
    const selectedWindows = (quote.windows || []).filter(w => w.selected);
    const vatRate = quote.vat_rate ?? 20;

    const vatBreakdown = (total) => {
      if (vatRate === 0) return { exVat: total, vatAmount: 0, incVat: total };
      const exVat = Math.round(total / 1.20 * 100) / 100;
      return { exVat, vatAmount: Math.round((total - exVat) * 100) / 100, incVat: total };
    };

    const computeMat = (matKey) => {
      const stdKey = `${matKey}_standard`;
      const upKey = `${matKey}_upfront`;
      // Try material-specific fields first, fall back to generic price
      const shutters = selectedWindows.reduce((sum, w) => sum + ((w[stdKey] ?? w.price ?? 0) || 0), 0);
      const upfront = selectedWindows.reduce((sum, w) => sum + ((w[upKey] ?? (w[stdKey] ?? w.price ?? 0) * 0.9) || 0), 0);
      const total = shutters + blindsTotal;
      const upTotal = Math.round((upfront + blindsTotal) * 100) / 100;
      const rrp = po?.[matKey]?.rrp_total || total;
      const discount = Math.round((rrp - total) * 100) / 100;
      return {
        standardTotal: total,
        deposit: Math.round(total * 0.6 * 100) / 100,
        balance: Math.round(total * 0.4 * 100) / 100,
        upfrontTotal: upTotal,
        saving: Math.round((total - upTotal) * 100) / 100,
        vat: vatBreakdown(total),
        rrp: Math.round(rrp * 100) / 100,
        discount: discount > 0.01 ? discount : 0,
        hasDiscount: discount > 0.01,
      };
    };

    // If pricing_options exist, compute for each available material
    if (po && Object.keys(po).length > 0) {
      const availableKeys = Object.keys(po);
      const materialPricing = {};
      availableKeys.forEach(key => {
        materialPricing[key] = computeMat(key);
      });

      // Use selectedMaterial if available, otherwise first key
      const effectiveMat = availableKeys.includes(selectedMaterial) ? selectedMaterial : availableKeys[0];
      const current = materialPricing[effectiveMat];

      return {
        ...current,
        blindsTotal,
        vatRate,
        upfrontVat: vatBreakdown(current?.upfrontTotal || 0),
        availableKeys,
        materialPricing,
      };
    }

    // Fallback for old quotes without pricing_options — use generic price
    const shuttersTotal = selectedWindows.reduce((sum, w) => sum + ((w.price ?? 0) || 0), 0);
    const total = shuttersTotal + blindsTotal;
    const upTotal = Math.round(total * 0.9 * 100) / 100;
    return {
      standardTotal: total,
      deposit: Math.round(total * 0.6 * 100) / 100,
      balance: Math.round(total * 0.4 * 100) / 100,
      upfrontTotal: upTotal,
      saving: Math.round((total - upTotal) * 100) / 100,
      vat: vatBreakdown(total),
      rrp: total,
      discount: 0,
      hasDiscount: false,
      blindsTotal,
      vatRate,
      upfrontVat: vatBreakdown(upTotal),
      availableKeys: [],
      materialPricing: {},
    };
  }, [quote, selectedMaterial]);

  // Charge amount based on selections
  const chargeAmount = useMemo(() => {
    if (!pricing) return 0;
    return paymentOption === 'full' ? pricing.upfrontTotal : pricing.deposit;
  }, [pricing, paymentOption]);

  // Toggle window selection
  const toggleWindowSelection = async (windowIndex) => {
    if (!quote || updating) return;
    setUpdating(true);
    const newWindows = quote.windows.map(w => ({ ...w, selected: w.index === windowIndex ? !w.selected : w.selected }));
    const selectedIndices = newWindows.filter(w => w.selected).map(w => w.index);
    try {
      const response = await fetch(`${API_URL}/api/interactive-quotes/update-selection/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_window_indices: selectedIndices })
      });
      const data = await response.json();
      if (data.success) {
        setQuote(prev => ({ ...prev, windows: data.data.windows, selected_total_price: data.data.selected_total_price, deposit_amount: data.data.deposit_amount, balance_amount: data.data.balance_amount }));
      }
    } catch (err) { console.error('Failed to update selection:', err); }
    finally { setUpdating(false); }
  };

  const handleConfirmClick = () => {
    const selectedCount = quote.windows.filter(w => w.selected).length;
    if (selectedCount === 0) { alert('Please select at least one window to proceed.'); return; }
    if (paymentOption === 'deposit') { setShowUpsell(true); }
    else { proceedToPayment('full'); }
  };

  const proceedToPayment = async (option) => {
    if (confirming) return;
    setShowUpsell(false);
    setConfirming(true);
    setPaymentError(null);
    try {
      const response = await fetch(`${API_URL}/api/interactive-quotes/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pay_full: option === 'full',
          selected_material: selectedMaterial,
          charge_amount: option === 'full' ? pricing.upfrontTotal : pricing.standardTotal
        })
      });
      const data = await response.json();
      if (data.success && data.data.checkout_id) {
        setPaymentOption(option);
        setCheckoutId(data.data.checkout_id);
        setShowPaymentWidget(true);
      } else { throw new Error(data.detail || 'Failed to create payment'); }
    } catch (err) {
      setPaymentError(err.message || 'Failed to process payment. Please try again.');
      setConfirming(false);
    }
  };

  const goBackToOptions = async () => {
    if (sumupCardRef.current) { try { sumupCardRef.current.unmount(); } catch (e) {} sumupCardRef.current = null; }
    try { await fetch(`${API_URL}/api/interactive-quotes/reset-confirm/${token}`, { method: 'POST' }); } catch (e) { /* ignore */ }
    setShowPaymentWidget(false); setCheckoutId(null); setConfirming(false); setPaymentStatus(null); setPaymentError(null);
  };

  // Loading
  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center pt-24">
      <div className="text-center"><Loader2 className="w-12 h-12 animate-spin text-[#8CB89F] mx-auto mb-4" /><p className="text-stone-600">Loading your quote...</p></div>
    </div>
  );

  // Error
  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center pt-24 px-4">
      <Card className="max-w-md w-full"><CardContent className="pt-8 text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-stone-800 mb-2">Unable to Load Quote</h2>
        <p className="text-stone-600 mb-6">{error}</p>
        <Button onClick={() => navigate('/')} variant="outline">Return to Homepage</Button>
      </CardContent></Card>
    </div>
  );

  // Payment success
  if (paymentStatus === 'success') return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center pt-24 px-4">
      <Card className="max-w-md w-full border-none shadow-xl"><CardContent className="pt-8 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-12 h-12 text-green-600" /></div>
        <h2 className="text-2xl font-bold text-stone-800 mb-2">Payment Successful!</h2>
        <p className="text-stone-600 mb-2">Your payment of <span className="font-semibold">£{chargeAmount?.toFixed(2)}</span> has been received.</p>
        <p className="text-stone-500 text-sm mb-6">You'll receive a confirmation email with your invoice shortly.</p>
        <div className="bg-stone-50 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-stone-500">Quote Reference</p>
          <p className="font-semibold text-stone-800">{quote?.quote_reference}</p>
          <p className="text-sm text-stone-500 mt-2">Material</p>
          <p className="font-semibold text-stone-800">{MATERIAL_CONFIG[selectedMaterial]?.label || selectedMaterial}</p>
          {paymentOption === 'deposit' && pricing?.balance > 0 && (
            <><p className="text-sm text-stone-500 mt-2">Balance remaining on installation</p>
            <p className="font-semibold text-stone-800">£{pricing.balance.toFixed(2)}</p></>
          )}
        </div>
        <Button onClick={() => navigate('/')} className="bg-[#8CB89F] hover:bg-[#7aa88d]">Return to Homepage</Button>
      </CardContent></Card>
    </div>
  );

  // Already confirmed
  if (quote?.status === 'confirmed' && !showPaymentWidget) return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center pt-24 px-4">
      <Card className="max-w-md w-full"><CardContent className="pt-8 text-center">
        <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-stone-800 mb-2">Quote Already Confirmed</h2>
        <p className="text-stone-600 mb-6">This quote has already been confirmed. If you have questions, please contact us.</p>
        <Button onClick={() => navigate('/contact')}>Contact Us</Button>
      </CardContent></Card>
    </div>
  );

  const selectedWindows = quote?.windows?.filter(w => w.selected) || [];
  const totalWindows = quote?.windows?.length || 0;
  const hasPricingOptions = !!quote?.pricing_options && Object.keys(quote.pricing_options).length > 0;
  const fmt = (n) => n?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00';

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-4">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#8CB89F]/10 rounded-full text-[#6a9a7d] text-sm font-medium mb-4">
            <ShieldCheck className="w-4 h-4" />Secure Quote Selection
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#2F4F4F] mb-2">Your Shutter Quote</h1>
          <p className="text-stone-600">Reference: <span className="font-semibold">{quote?.quote_reference}</span></p>
        </div>

        {/* Customer Info */}
        <Card className="mb-6 border-none shadow-lg"><CardContent className="pt-6">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div><p className="text-stone-500">Customer</p><p className="font-semibold text-stone-800">{quote?.customer_name}</p></div>
            <div><p className="text-stone-500">Email</p><p className="font-semibold text-stone-800">{quote?.customer_email}</p></div>
            {quote?.customer_address && (<div className="md:col-span-2"><p className="text-stone-500">Installation Address</p><p className="font-semibold text-stone-800">{quote?.customer_address}</p></div>)}
          </div>
        </CardContent></Card>

        {/* Product Specs */}
        <Card className="mb-6 border-none shadow-lg"><CardHeader className="pb-2"><CardTitle className="text-lg text-[#2F4F4F]">Product Specifications</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><p className="text-stone-500">Louvre Size</p><p className="font-semibold text-stone-800">{quote?.louvre_size}</p></div>
              <div><p className="text-stone-500">Colour</p><p className="font-semibold text-stone-800">{quote?.colour}</p></div>
              <div><p className="text-stone-500">Control Type</p><p className="font-semibold text-stone-800">{quote?.control_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Hidden Tilt Rod'}</p></div>
              <div><p className="text-stone-500">Stiles</p><p className="font-semibold text-stone-800">{quote?.stiles?.charAt(0).toUpperCase() + quote?.stiles?.slice(1) || 'Beaded'}</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Window Selection */}
        {!showPaymentWidget && (
          <Card className="mb-6 border-none shadow-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-[#2F4F4F]">Select Your Windows</CardTitle>
                <Badge variant="secondary" className="bg-[#8CB89F]/10 text-[#6a9a7d]">{selectedWindows.length} of {totalWindows} selected</Badge>
              </div>
              <p className="text-sm text-stone-500 mt-1">Choose which windows you'd like to proceed with.</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {quote?.windows?.map((window) => (
                  <div key={window.index} onClick={() => toggleWindowSelection(window.index)}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${window.selected ? 'border-[#8CB89F] bg-[#8CB89F]/5' : 'border-stone-200 bg-stone-50 opacity-60'}`}
                    data-testid={`window-${window.index}`}>
                    <div className="flex items-center gap-4">
                      <Checkbox checked={window.selected} onCheckedChange={() => toggleWindowSelection(window.index)} className="data-[state=checked]:bg-[#8CB89F] data-[state=checked]:border-[#8CB89F]" />
                      <div>
                        <p className={`font-semibold ${window.selected ? 'text-stone-800' : 'text-stone-500'}`}>{window.label}</p>
                        {window.layout_code && <span className="text-xs text-stone-500 font-mono bg-stone-100 px-2 py-0.5 rounded">{window.layout_code}</span>}
                      </div>
                    </div>
                    {/* Show price for selected material */}
                    <div className={`text-lg font-bold ${window.selected ? 'text-[#2F4F4F]' : 'text-stone-400 line-through'}`}>
                      £{fmt(selectedMaterial === 'basswood' ? (window.basswood_standard || window.price) : (window.paulownia_standard || window.price))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-4">
                <Button variant="outline" size="sm" onClick={async () => { setUpdating(true); try { const r = await fetch(`${API_URL}/api/interactive-quotes/update-selection/${token}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selected_window_indices: quote.windows.map(w => w.index) }) }); const d = await r.json(); if (d.success) setQuote(prev => ({ ...prev, windows: d.data.windows, selected_total_price: d.data.selected_total_price, deposit_amount: d.data.deposit_amount, balance_amount: d.data.balance_amount })); } finally { setUpdating(false); } }} disabled={updating}>Select All</Button>
                <Button variant="outline" size="sm" onClick={async () => { setUpdating(true); try { const r = await fetch(`${API_URL}/api/interactive-quotes/update-selection/${token}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selected_window_indices: [] }) }); const d = await r.json(); if (d.success) setQuote(prev => ({ ...prev, windows: d.data.windows, selected_total_price: d.data.selected_total_price, deposit_amount: d.data.deposit_amount, balance_amount: d.data.balance_amount })); } finally { setUpdating(false); } }} disabled={updating}>Deselect All</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Blinds */}
        {!showPaymentWidget && quote?.blinds?.length > 0 && (
          <Card className="mb-6 border-none shadow-lg"><CardHeader className="pb-2"><CardTitle className="text-lg text-[#2F4F4F]">Blinds</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {quote.blinds.map((blind, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 rounded-xl border-2 border-[#8CB89F] bg-[#8CB89F]/5">
                    <div><p className="font-semibold text-stone-800">{blind.room_name}</p>{blind.description && <p className="text-xs text-stone-500 mt-0.5">{blind.description}</p>}</div>
                    <div className="text-lg font-bold text-[#2F4F4F]">£{fmt(blind.price)}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-3 pt-3 border-t border-stone-100 text-sm font-semibold">
                <span className="text-stone-600">Blinds Total</span>
                <span className="text-[#2F4F4F]">£{fmt(pricing?.blindsTotal)}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== MATERIAL & PAYMENT SELECTION ===== */}
        {!showPaymentWidget && hasPricingOptions && (
          <div className="mb-6">
            {/* Multiple materials — show selection cards */}
            {pricing?.availableKeys?.length > 1 && (
              <>
                <h3 className="text-lg font-semibold text-[#2F4F4F] mb-4 text-center">1. Choose Your Material</h3>
                <div className={`grid gap-4 mb-8 ${(pricing.availableKeys.length) >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                  {pricing.availableKeys.map(matKey => {
                    const config = MATERIAL_CONFIG[matKey] || { label: matKey, desc: '10 year warranty', color: '#8CB89F', accentBg: 'bg-gray-50', accentBorder: 'border-gray-200', accentText: 'text-gray-700', badge: null };
                    const matPricing = pricing.materialPricing[matKey];
                    if (!matPricing) return null;
                    const isSelected = selectedMaterial === matKey;

                    return (
                      <div key={matKey} onClick={() => setSelectedMaterial(matKey)}
                        className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all ${isSelected ? `${config.accentBg} shadow-lg scale-[1.01]` : 'border-stone-200 bg-white hover:border-stone-300'}`}
                        style={isSelected ? { borderColor: config.color } : {}}
                        data-testid={`material-${matKey}`}>
                        {config.badge && <div className="absolute -top-3 right-4 px-3 py-0.5 text-white text-xs font-semibold rounded-full" style={{ backgroundColor: config.color }}>{config.badge}</div>}
                        {isSelected && <div className="absolute -top-3 left-4 px-3 py-0.5 text-white text-xs font-semibold rounded-full" style={{ backgroundColor: config.color }}>Selected</div>}
                        <div className="flex items-start justify-between mb-2">
                          <div><h4 className="font-bold text-stone-800 text-lg">{config.label}</h4><p className="text-xs text-stone-500 mt-0.5">{config.desc}</p></div>
                          <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1" style={isSelected ? { borderColor: config.color, backgroundColor: config.color } : {}}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <div className="space-y-3 mt-4">
                          <div className="bg-stone-50 rounded-lg p-3">
                            <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Standard (60% deposit, 40% on installation)</p>
                            <div className="flex justify-between items-baseline">
                              <span className="text-sm text-stone-600">Total</span>
                              <span className="text-xl font-bold text-[#2F4F4F]">£{fmt(matPricing.standardTotal)}</span>
                            </div>
                            {matPricing.hasDiscount && (
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-stone-400 line-through">RRP: £{fmt(matPricing.rrp)}</span>
                                <span className="text-green-600 font-semibold">You save £{fmt(matPricing.discount)}</span>
                              </div>
                            )}
                            {pricing.vatRate === 0 && (
                              <p className="text-xs text-amber-600 mt-1 font-medium">VAT zero rated (qualifying new build)</p>
                            )}
                            <div className="flex justify-between text-xs text-stone-400 mt-1">
                              <span>Deposit: £{fmt(matPricing.deposit)}</span>
                              <span>Balance: £{fmt(matPricing.balance)}</span>
                            </div>
                          </div>
                          <div className={`${config.accentBg} rounded-lg p-3 border ${config.accentBorder}`}>
                            <p className={`text-[10px] uppercase tracking-wider ${config.accentText} mb-1`}>Pay Upfront</p>
                            <div className="flex justify-between items-baseline">
                              <span className={`text-sm ${config.accentText} font-medium`}>One payment</span>
                              <span className="text-xl font-bold text-[#2F4F4F]">£{fmt(matPricing.upfrontTotal)}</span>
                            </div>
                            <p className={`text-xs ${config.accentText} mt-1`}>Save £{fmt(matPricing.saving)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Single material — clean price display, no selection needed */}
            {pricing?.availableKeys?.length === 1 && (() => {
              const matKey = pricing.availableKeys[0];
              const config = MATERIAL_CONFIG[matKey] || { label: matKey, color: '#8CB89F' };
              const matPricing = pricing.materialPricing[matKey];
              if (!matPricing) return null;
              return (
                <div className="mb-8">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-[#2F4F4F]">Your Quote — {config.label} Shutters</h3>
                    <div className="mt-3">
                      <span className="text-4xl font-bold text-[#2F4F4F]">£{fmt(matPricing.standardTotal)}</span>
                    </div>
                    {matPricing.hasDiscount && (
                      <div className="mt-1">
                        <span className="text-stone-400 line-through text-sm mr-2">RRP: £{fmt(matPricing.rrp)}</span>
                        <span className="text-green-600 font-semibold text-sm">You save £{fmt(matPricing.discount)}</span>
                      </div>
                    )}
                    {pricing.vatRate === 0 && (
                      <p className="text-xs text-amber-600 mt-1 font-medium">VAT zero rated (qualifying new build)</p>
                    )}
                    <p className="text-xs text-stone-400 mt-1">Including survey, installation & VAT</p>
                  </div>
                </div>
              );
            })()}

            {/* Payment Method */}
            <h3 className="text-lg font-semibold text-[#2F4F4F] mb-4 text-center">
              {pricing?.availableKeys?.length > 1 ? '2. ' : ''}Choose Payment Method
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Deposit */}
              <div onClick={() => setPaymentOption('deposit')}
                className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all ${paymentOption === 'deposit' ? 'border-[#2F4F4F] bg-white shadow-lg scale-[1.01]' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                data-testid="option-deposit">
                {paymentOption === 'deposit' && <div className="absolute -top-3 left-4 px-3 py-0.5 bg-[#2F4F4F] text-white text-xs font-semibold rounded-full">Selected</div>}
                <div className="flex items-start justify-between mb-3">
                  <div><h4 className="font-semibold text-stone-800">Pay Deposit</h4><p className="text-xs text-stone-500 mt-0.5">60% now, 40% on installation</p></div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentOption === 'deposit' ? 'border-[#2F4F4F] bg-[#2F4F4F]' : 'border-stone-300'}`}>
                    {paymentOption === 'deposit' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-stone-600">Due now</span>
                    <span className="text-2xl font-bold text-[#2F4F4F]">£{fmt(pricing?.deposit)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-stone-400">
                    <span>Balance on installation</span><span>£{fmt(pricing?.balance)}</span>
                  </div>
                  <div className="pt-2 border-t border-stone-100 flex justify-between text-sm">
                    <span className="text-stone-500">Total</span>
                    <span className="font-semibold text-stone-700">£{fmt(pricing?.standardTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Pay Upfront */}
              <div onClick={() => setPaymentOption('full')}
                className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all ${paymentOption === 'full' ? 'border-[#8CB89F] bg-[#8CB89F]/5 shadow-lg scale-[1.01]' : 'border-stone-200 bg-white hover:border-[#8CB89F]/50'}`}
                data-testid="option-full">
                <div className="absolute -top-3 right-4 px-3 py-0.5 bg-[#8CB89F] text-white text-xs font-semibold rounded-full">
                  Best Value — Save £{fmt(pricing?.saving)}
                </div>
                {paymentOption === 'full' && <div className="absolute -top-3 left-4 px-3 py-0.5 bg-[#8CB89F] text-white text-xs font-semibold rounded-full">Selected</div>}
                <div className="flex items-start justify-between mb-3">
                  <div><h4 className="font-semibold text-stone-800">Pay Upfront</h4><p className="text-xs text-[#6a9a7d] font-medium mt-0.5">No balance due on installation</p></div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentOption === 'full' ? 'border-[#8CB89F] bg-[#8CB89F]' : 'border-stone-300'}`}>
                    {paymentOption === 'full' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-stone-600">One payment</span>
                    <span className="text-2xl font-bold text-[#2F4F4F]">£{fmt(pricing?.upfrontTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-400 line-through">Was £{fmt(pricing?.standardTotal)}</span>
                    <span className="text-[#6a9a7d] font-semibold">You save £{fmt(pricing?.saving)}</span>
                  </div>
                  <div className="pt-2 border-t border-stone-100 text-sm text-[#6a9a7d]">No further payments — everything included</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fallback for old quotes without pricing_options */}
        {!showPaymentWidget && !hasPricingOptions && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#2F4F4F] mb-4 text-center">Choose Payment Method</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div onClick={() => setPaymentOption('deposit')} className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all ${paymentOption === 'deposit' ? 'border-[#2F4F4F] bg-white shadow-lg' : 'border-stone-200'}`} data-testid="option-deposit">
                <h4 className="font-semibold text-stone-800 mb-2">Pay Deposit</h4>
                <p className="text-2xl font-bold text-[#2F4F4F]">£{fmt(pricing?.deposit)}</p>
                <p className="text-sm text-stone-400">+ £{fmt(pricing?.balance)} on installation</p>
              </div>
              <div onClick={() => setPaymentOption('full')} className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all ${paymentOption === 'full' ? 'border-[#8CB89F] bg-[#8CB89F]/5 shadow-lg' : 'border-stone-200'}`} data-testid="option-full">
                <h4 className="font-semibold text-stone-800 mb-2">Pay Upfront</h4>
                <p className="text-2xl font-bold text-[#2F4F4F]">£{fmt(pricing?.upfrontTotal)}</p>
                <p className="text-sm text-[#6a9a7d]">Save £{fmt(pricing?.saving)}</p>
              </div>
            </div>
          </div>
        )}

        {/* SumUp Widget */}
        {showPaymentWidget && (
          <Card className="mb-6 border-none shadow-xl" data-testid="payment-widget-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-[#2F4F4F] flex items-center gap-2"><CreditCard className="w-5 h-5" />Enter Card Details</CardTitle>
                  <p className="text-sm text-stone-500 mt-1">
                    {MATERIAL_CONFIG[selectedMaterial]?.label || selectedMaterial} — £{chargeAmount?.toFixed(2)} will be charged securely.
                  </p>
                </div>
                {!paymentStatus && <Button variant="outline" size="sm" onClick={goBackToOptions} className="text-stone-500">Change Option</Button>}
              </div>
            </CardHeader>
            <CardContent>
              {paymentStatus === 'verifying' ? (
                <div className="text-center py-8"><Loader2 className="w-10 h-10 animate-spin text-[#8CB89F] mx-auto mb-3" /><p className="text-stone-600">Verifying your payment...</p></div>
              ) : paymentStatus === 'error' ? (
                <div className="text-center py-6">
                  <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" /><p className="text-red-600 mb-4">{paymentError}</p>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => { setPaymentStatus(null); setPaymentError(null); }} variant="outline">Try Again</Button>
                    <Button onClick={goBackToOptions} variant="outline" className="text-stone-500">Change Option</Button>
                  </div>
                </div>
              ) : (<div id="sumup-card-widget" ref={sumupWidgetRef} className="min-h-[300px]" />)}
            </CardContent>
          </Card>
        )}

        {/* Upsell Modal */}
        {showUpsell && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="upsell-modal">
            <Card className="max-w-md w-full border-none shadow-2xl animate-in fade-in zoom-in-95 duration-200">
              <CardContent className="pt-8 pb-6 px-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#8CB89F]/10 rounded-full flex items-center justify-center mx-auto mb-4"><CreditCard className="w-8 h-8 text-[#8CB89F]" /></div>
                  <h3 className="text-xl font-bold text-[#2F4F4F] mb-2">Save £{fmt(pricing?.saving)}!</h3>
                  <p className="text-stone-600 text-sm">Pay upfront for your {selectedMaterial === 'basswood' ? 'Basswood' : 'Paulownia'} shutters and save.</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-4 mb-6 space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-stone-500">Deposit option</span>
                    <span className="font-semibold text-stone-700">£{fmt(pricing?.standardTotal)}</span>
                  </div>
                  <div className="border-t border-stone-200" />
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#6a9a7d] font-medium">Pay upfront today</span>
                    <span className="text-[#2F4F4F] font-bold text-lg">£{fmt(pricing?.upfrontTotal)}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <Button onClick={() => proceedToPayment('full')} disabled={confirming} className="w-full bg-[#8CB89F] hover:bg-[#7aa88d] text-white py-6 text-base rounded-xl shadow-md" data-testid="upsell-pay-full-btn">
                    {confirming ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
                    Yes, Pay £{fmt(pricing?.upfrontTotal)} & Save
                  </Button>
                  <Button onClick={() => proceedToPayment('deposit')} disabled={confirming} variant="outline" className="w-full py-5 text-sm text-stone-500 rounded-xl" data-testid="upsell-deposit-btn">
                    No thanks, pay £{fmt(pricing?.deposit)} deposit
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Confirm Button */}
        {!showPaymentWidget && !showUpsell && (
          <div className="text-center">
            <Button onClick={handleConfirmClick} disabled={confirming || selectedWindows.length === 0}
              className={`text-white px-12 py-6 text-lg rounded-full shadow-lg transition-all ${paymentOption === 'full' ? 'bg-[#8CB89F] hover:bg-[#7aa88d]' : 'bg-[#2F4F4F] hover:bg-[#3d6363]'}`}
              data-testid="confirm-and-pay-btn">
              {confirming ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>) : paymentOption === 'full' ? (<><CreditCard className="w-5 h-5 mr-2" />Pay £{fmt(pricing?.upfrontTotal)} Upfront</>) : (<><CreditCard className="w-5 h-5 mr-2" />Pay £{fmt(pricing?.deposit)} Deposit</>)}
            </Button>
            {selectedWindows.length === 0 && <p className="text-red-500 text-sm mt-3">Please select at least one window to proceed</p>}
            {paymentError && !showPaymentWidget && <p className="text-red-500 text-sm mt-3">{paymentError}</p>}
            <p className="text-stone-500 text-sm mt-4 flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4" />Secure payment powered by SumUp</p>
          </div>
        )}

        {/* Trust */}
        <div className="mt-12 grid grid-cols-3 gap-4 text-center">
          <div className="p-4"><Home className="w-8 h-8 text-[#8CB89F] mx-auto mb-2" /><p className="text-sm font-medium text-stone-700">Survey Included</p><p className="text-xs text-stone-500">Professional measurement</p></div>
          <div className="p-4"><ShieldCheck className="w-8 h-8 text-[#8CB89F] mx-auto mb-2" /><p className="text-sm font-medium text-stone-700">10 Year Warranty</p><p className="text-xs text-stone-500">Structural guarantee</p></div>
          <div className="p-4"><Clock className="w-8 h-8 text-[#8CB89F] mx-auto mb-2" /><p className="text-sm font-medium text-stone-700">Quote Valid 30 Days</p><p className="text-xs text-stone-500">Prices guaranteed</p></div>
        </div>

        {quote?.customer_notes && (
          <Card className="mt-8 border-none shadow-lg bg-amber-50"><CardContent className="pt-6">
            <p className="text-sm font-medium text-amber-800 mb-2">Notes from Your Surveyor</p>
            <p className="text-amber-700 whitespace-pre-wrap">{quote.customer_notes}</p>
          </CardContent></Card>
        )}

        <div className="mt-8"><TermsAndConditions collapsed={true} /></div>
      </div>
    </div>
  );
};

export default QuoteSelectionPage;
